import React from 'react';
import { Navigate, Route, Routes as ReactRoutes } from 'react-router-dom';

import DashboardHome from './pages/DashboardHome';
import FarmDashboard from './pages/FarmDashboard';
import VeterinaryDashboard from './pages/VeterinaryDashboard';
import AnimalSearch from './pages/AnimalSearch';
import AnimalView from './pages/AnimalView';
import OperationalStrategy from './pages/OperationalStrategy';
import Login from './pages/Login';
import LoginNew from './pages/LoginNew';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation';
import FarmSettingsPage from './pages/FarmSettingsPage';
import LungWeatherReview from './pages/LungWeatherReview';
import SystemSettings from './pages/SystemSettings';
import SystemData from './pages/SystemData';
import FarmInfo from './pages/FarmInfo';
import EmissionSourceUsage from './pages/EmissionSourceUsage';
import MedicalInformation from './pages/MedicalInformation';
import LocationsPage from './pages/LocationsPage';
import LocationView from './pages/LocationView';
import SiloView from './pages/SiloView';
import TasksPage from './pages/TasksPage';
import UserView from './pages/UserView';
import VeterinaryView from './pages/VeterinaryView';
import VeterinaryOrdersPage from './pages/VeterinaryOrdersPage';
import useAuthContext from './hooks/useAuthContext';
import DevicesPage from './pages/Devices';
import HealthInjectorView from './pages/HealthInjectorView';
import VetMedicalSuppliesPage from './pages/VetMedicalSuppliesPage';
import VetBottlesPage from './pages/VetBottlesPages';
import MedicationCatalogue from './pages/MedicationCatalogue';
import VetLabelsPage from './pages/VetLabelsPage';
import DetailedList from './pages/Analytics/detailed-list';
import Analytics from './pages/Analytics';
import VeterinaryLocations from './pages/VeterinaryLocations';
import LocationClimateViewV2 from './pages/LocationClimateViewV2';
import TasksExecution from './pages/TasksExecution';
import VetVaccinationRulesPage from './pages/VetVaccinationRulesPage';
import UserRole from './constants/user-role';
import UserManagementView from './pages/UserManagementView';
import AnimalCompareSearch from './pages/AnimalCompareSearch';
import AnimalReports from './pages/AnimalReports';
import SlaughterPreparation from './pages/SlaughterPreparation';
import ErrorPage from './pages/ErrorPage';
import { SlaughterhouseManagementView } from './pages/SlaughterhouseManagementView';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { Permission } from './constants/permissions.ts';
import { FeatureType } from './hooks/features.ts';
import { DASHBOARD_PATH, LEGACY_FARMS_OVERVIEW_PATH } from './models/dashboard.ts';

const Routes: React.FC = () => {
  const { state } = useAuthContext();
  const isVet = state.role === UserRole.veterinary;

  return (
    <div className="app-wrapper">
      <ReactRoutes>
        <Route path="/error" element={<ErrorPage />} />

        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<LoginNew />} />
        <Route
          path="/forgot-password"
          element={
            <ProtectedRoute requireGuest>
              <ForgotPassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <ProtectedRoute requireGuest>
              <ResetPassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accept-invitation"
          element={
            <ProtectedRoute requireGuest>
              <AcceptInvitation />
            </ProtectedRoute>
          }
        />

        {/* One landing route for everyone. `DashboardHome` decides between the farms overview and the
            single-farm dashboard; users without the overview permission only ever get the latter. */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute permission={isVet ? Permission.VIEW_VETERINARY_DASHBOARD : Permission.VIEW_DASHBOARD}>
              {isVet ? <VeterinaryDashboard /> : <DashboardHome />}
            </ProtectedRoute>
          }
        />

        {/* The overview used to live on its own path. Kept as a redirect so older links still land on it. */}
        <Route path={LEGACY_FARMS_OVERVIEW_PATH} element={<Navigate to={DASHBOARD_PATH} replace />} />

        {/* The same dashboard sourced from one of the queue's farms. Declared after /dashboard so the bare
            path still resolves to the landing route rather than being read as a farm id. */}
        <Route
          path="/dashboard/:farmId"
          element={
            <ProtectedRoute permission={Permission.VIEW_FARMS_OVERVIEW}>
              <FarmDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/farm-settings/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_FARM_SETTINGS}>
              <FarmSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/farm-settings" element={<Navigate to="/farm-settings/dashboard" />} />

        {/* Reached from the lung/weather rule card in farm settings, so it shares that page's permission. The
            feature flag hides the whole rule type, this page included. */}
        <Route
          path="/lung-weather/:diseaseId"
          element={
            <ProtectedRoute permission={Permission.VIEW_FARM_SETTINGS} feature={FeatureType.USE_LUNG_WEATHER_RULE}>
              <LungWeatherReview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_SYSTEM_SETTINGS}>
              <SystemSettings />
            </ProtectedRoute>
          }
        />
        <Route path="/settings" element={<Navigate to="/settings/farms" />} />

        <Route
          path="/devices/injectors/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_DEVICES}>
              <HealthInjectorView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/devices/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_DEVICES}>
              <DevicesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/devices" element={<Navigate to="/devices/injectors" />} />

        <Route
          path="/system/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_SYSTEM_DATA}>
              <SystemData />
            </ProtectedRoute>
          }
        />
        <Route path="/system" element={<Navigate to="/system/logs" />} />

        {/* Ahead of /farm/:option, which stays Admin and Manager - the documents tab is the one an export
            notification links to, so it is open to plain users as well. */}
        <Route
          path="/farm/documents"
          element={
            <ProtectedRoute permission={Permission.VIEW_FARM_DOCUMENTS}>
              <FarmInfo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/farm/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_FARM_INFO}>
              <FarmInfo />
            </ProtectedRoute>
          }
        />
        <Route path="/farm" element={<Navigate to="/farm/documents" />} />

        <Route
          path="/emission-source-usage"
          element={
            <ProtectedRoute permission={Permission.VIEW_EMISSION_SOURCES}>
              <EmissionSourceUsage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/slaughter-declarations/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_SLAUGHTER_PREPARATION}>
              <SlaughterPreparation />
            </ProtectedRoute>
          }
        />
        <Route path="/slaughter-declarations" element={<Navigate to="/slaughter-declarations/create" />} />

        <Route
          path="/tasks/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_TASKS}>
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route path="/tasks" element={<Navigate to="/tasks/lists" />} />

        <Route
          path="/tasks-execution/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_TASKS}>
              <TasksExecution />
            </ProtectedRoute>
          }
        />
        <Route path="/tasks-execution" element={<Navigate to="/tasks-execution/lists" />} />

        <Route
          path="/medical-info/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_MEDICAL_INFO}>
              <MedicalInformation />
            </ProtectedRoute>
          }
        />
        <Route path="/medical-info" element={<Navigate to="/medical-info/orders" />} />

        <Route
          path="/locations"
          element={
            <ProtectedRoute permission={Permission.VIEW_LOCATIONS}>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations/feed"
          element={
            <ProtectedRoute permission={Permission.VIEW_LOCATIONS}>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations/silos"
          element={
            <ProtectedRoute permission={Permission.VIEW_FEED_SILOS}>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations/silos/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_FEED_SILOS}>
              <SiloView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations/:id/:tab?"
          element={
            <ProtectedRoute permission={Permission.VIEW_LOCATIONS}>
              <LocationView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations/:id/climate"
          element={
            <ProtectedRoute permission={Permission.VIEW_LOCATION_CLIMATE}>
              <LocationClimateViewV2 />
            </ProtectedRoute>
          }
        />

        <Route
          path="/animal"
          element={
            <ProtectedRoute permission={Permission.VIEW_ANIMAL_SEARCH}>
              <AnimalSearch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/animal/compare"
          element={
            <ProtectedRoute permission={Permission.VIEW_ANIMAL_COMPARE}>
              <AnimalCompareSearch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/animal/reports"
          element={
            <ProtectedRoute permission={Permission.VIEW_ANIMAL_REPORTS}>
              <AnimalReports />
            </ProtectedRoute>
          }
        />

        <Route
          path="/animal/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_ANIMAL_SEARCH}>
              <AnimalView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/:id"
          element={
            <ProtectedRoute requireAuth>
              <UserView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/labels"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_LABELS}>
              <VetLabelsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user-management/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_USER_MANAGEMENT}>
              <UserManagementView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/slaughterhouse-management/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_SLAUGHTERHOUSE_MANAGEMENT}>
              <SlaughterhouseManagementView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/veterinary/:id"
          element={
            <ProtectedRoute requireAuth>
              <VeterinaryView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/locations"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_LOCATIONS}>
              <VeterinaryLocations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vet/locations/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_ORDERS}>
              <VeterinaryOrdersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/orders"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_ORDERS}>
              <VeterinaryOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vet/orders/:id"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_ORDERS}>
              <VeterinaryOrdersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/vaccination-rules"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_VACCINATION_RULES}>
              <VetVaccinationRulesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/medical-supplies"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_MEDICAL_SUPPLIES}>
              <VetMedicalSuppliesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/bottles"
          element={
            <ProtectedRoute permission={Permission.VIEW_VET_BOTTLES}>
              <VetBottlesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/vet/medications"
          element={
            <ProtectedRoute permission={Permission.VIEW_MEDICATION_CATALOGUE}>
              <MedicationCatalogue />
            </ProtectedRoute>
          }
        />

        <Route
          path="/strategy/:area/:option"
          element={
            <ProtectedRoute permission={Permission.VIEW_OPERATIONAL_STRATEGY}>
              <OperationalStrategy />
            </ProtectedRoute>
          }
        />
        <Route path="/strategy" element={<Navigate to="/strategy/injections/0" />} />

        <Route path="/analytics/:locations" element={<Analytics />} />
        <Route path="/analytics/detailed/:locations" element={<DetailedList />} />

        <Route path="/" element={<Navigate to="/dashboard" />} />
      </ReactRoutes>
    </div>
  );
};

export default Routes;
